import React, { useState, useEffect } from 'react';
import { Card, Button, Form, InputGroup, Spinner, Modal, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  fetchSubOrganizations,
  fetchSubOrgManagers,
  createSubOrganization,
  updateSubOrganization,
  deleteSubOrganization,
  fetchSubOrgDropdown
} from '../../../actioncreators/actioncreators';
import './SubOrgManager.css';
import { useSuspension } from '../../../contexts/SuspensionContext';

// Icons
const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
  </svg>
);

const FolderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
  </svg>
);

const ViewIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
  </svg>
);

const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);

const LocationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
  </svg>
);

const UsersIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
  </svg>
);

const SubOrgManager = () => {
  const navigate = useNavigate();
  // Suspension check
  const { isSuspended } = useSuspension();
  // Super Admin viewing a specific organization's Sub-Organizations tab.
  // Every call below must pass this through, otherwise the backend falls
  // back to the Super Admin's own org_id and every organization's tab shows
  // identical (Super Admin's own) data -- including delete, which would
  // silently affect zero rows for a sub-org in a different org.
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const [subOrgs, setSubOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSubOrg, setSelectedSubOrg] = useState(null);
  const [managers, setManagers] = useState([]);
  const [parentSubOrgs, setParentSubOrgs] = useState([]);
  const [formData, setFormData] = useState({
    sub_org_name: '',
    sub_org_code: '',
    parent_sub_org_id: '',
    manager_user_id: '',
    description: ''
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  // Page-level banners (used for actions that happen outside the create/edit modal,
  // e.g. delete — where the in-modal alert would not be visible).
  const [pageSuccess, setPageSuccess] = useState('');
  const [pageError, setPageError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
    loadManagers();
    loadParentSubOrgs();
  }, [statusFilter, superAdminSelectedOrgId]);

  // Auto-dismiss the page-level banners so they don't linger.
  useEffect(() => {
    if (!pageSuccess && !pageError) return;
    const t = setTimeout(() => {
      setPageSuccess('');
      setPageError('');
    }, 4000);
    return () => clearTimeout(t);
  }, [pageSuccess, pageError]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchSubOrganizations(statusFilter || null, superAdminSelectedOrgId);
      setSubOrgs(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch sub-organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadManagers = async () => {
    try {
      const response = await fetchSubOrgManagers(superAdminSelectedOrgId);
      setManagers(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch managers:', error);
    }
  };

  const loadParentSubOrgs = async () => {
    try {
      const response = await fetchSubOrgDropdown(superAdminSelectedOrgId);
      setParentSubOrgs(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch parent sub-orgs:', error);
    }
  };

  const handleOpenModal = (subOrg = null) => {
    setFormError('');
    setFormSuccess('');
    if (subOrg) {
      setSelectedSubOrg(subOrg);
      setFormData({
        sub_org_name: subOrg.sub_org_name || '',
        sub_org_code: subOrg.sub_org_code || '',
        parent_sub_org_id: subOrg.parent_sub_org_id || '',
        manager_user_id: subOrg.manager_user_id || '',
        description: subOrg.description || ''
      });
    } else {
      setSelectedSubOrg(null);
      setFormData({
        sub_org_name: '',
        sub_org_code: '',
        parent_sub_org_id: '',
        manager_user_id: '',
        description: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSubOrg(null);
    setFormError('');
    setFormSuccess('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    setSubmitting(true);

    if (!formData.sub_org_name.trim()) {
      setFormError('Sub-Organization name is required');
      setSubmitting(false);
      return;
    }

    // A manager is mandatory when creating a sub-organization (matches backend).
    if (!selectedSubOrg && !formData.manager_user_id) {
      setFormError('Please select a manager for this sub-organization.');
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        sub_org_name: formData.sub_org_name.trim(),
        sub_org_code: formData.sub_org_code.trim() || null,
        parent_sub_org_id: formData.parent_sub_org_id || null,
        manager_user_id: formData.manager_user_id || null,
        description: formData.description.trim() || null,
        org_id: superAdminSelectedOrgId || null
      };

      // The backend returns HTTP 200 even for a functional failure (e.g. "code
      // already in use") - it does not reject at the HTTP level, so axios
      // resolves normally and the catch block below never fires for these.
      // Without checking businessStatusCode here, a failed create/update was
      // silently reported as success (modal closed, "created successfully"
      // shown) while nothing actually changed.
      let response;
      if (selectedSubOrg) {
        response = await updateSubOrganization(selectedSubOrg.sub_org_id, payload);
      } else {
        response = await createSubOrganization(payload);
      }

      if (response?.data?.businessStatusCode !== 2) {
        setFormError(response?.data?.message || 'An error occurred');
        return;
      }

      setFormSuccess(selectedSubOrg ? 'Sub-Organization updated successfully' : 'Sub-Organization created successfully');

      setTimeout(() => {
        handleCloseModal();
        fetchData();
        loadParentSubOrgs();
      }, 1500);
    } catch (error) {
      console.error('Sub-org update error:', error);
      const errorMsg = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'An error occurred';
      setFormError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDeleteModal = (subOrg) => {
    setSelectedSubOrg(subOrg);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedSubOrg) return;
    setSubmitting(true);
    const deletedName = selectedSubOrg.sub_org_name;
    try {
      const response = await deleteSubOrganization(selectedSubOrg.sub_org_id, superAdminSelectedOrgId);
      // The backend returns functional failures (e.g. "has active locations") as
      // HTTP 200 with businessStatusCode !== 2. Only treat code 2 as a real
      // delete; otherwise surface the message as an error instead of a false
      // "deleted successfully".
      if (response?.data?.businessStatusCode !== 2) {
        setShowDeleteModal(false);
        setSelectedSubOrg(null);
        setPageError(response?.data?.message || 'Could not delete sub-organization');
        return;
      }
      setShowDeleteModal(false);
      setSelectedSubOrg(null);
      // Show the success confirmation on the page (the delete modal is now closed,
      // so the in-modal alert used by create/update is not visible here).
      setPageSuccess(
        response?.data?.message ||
        `Sub-organization "${deletedName}" deleted successfully`
      );
      fetchData();
      loadParentSubOrgs();
    } catch (error) {
      console.error('Failed to delete sub-organization:', error);
      setPageError(error.response?.data?.message || 'Failed to delete sub-organization');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredSubOrgs = subOrgs.filter(so =>
    so.sub_org_name?.toLowerCase().includes(search.toLowerCase()) ||
    so.sub_org_code?.toLowerCase().includes(search.toLowerCase()) ||
    so.manager_name?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status) => {
    if (status === 'ACTIVE') {
      return <span className="status-pill status-active">Active</span>;
    }
    return <span className="status-pill status-inactive">Inactive</span>;
  };

  return (
    <div className="sub-org-manager-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-icon">
            <FolderIcon />
          </div>
          <div>
            <h1 className="page-title">Sub-Organizations</h1>
            <p className="page-subtitle">Manage departments, units, and divisions</p>
          </div>
        </div>
        <Button
          className="btn-create"
          onClick={isSuspended ? undefined : () => handleOpenModal()}
          disabled={isSuspended}
          style={isSuspended ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          title={isSuspended ? 'Actions are disabled - organization is suspended' : ''}
        >
          <PlusIcon />
          <span>Add Sub-Organization</span>
        </Button>
      </div>

      {/* Page-level action feedback (e.g. delete confirmation) */}
      {pageSuccess && (
        <Alert variant="success" dismissible onClose={() => setPageSuccess('')}>
          {pageSuccess}
        </Alert>
      )}
      {pageError && (
        <Alert variant="danger" dismissible onClose={() => setPageError('')}>
          {pageError}
        </Alert>
      )}

      {/* Filters */}
      <Card className="filter-card">
        <Card.Body>
          <Row className="g-2 align-items-center">
            <Col lg={6} md={12}>
              <InputGroup className="search-input-group">
                <InputGroup.Text><SearchIcon /></InputGroup.Text>
                <Form.Control
                  placeholder="Search by name, code, or manager..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col lg={4} md={6}>
              <Form.Select
                className="filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </Form.Select>
            </Col>
            <Col lg={2} md={6} className="d-flex justify-content-end">
              <Button
                variant="link"
                className="btn-clear-filters"
                onClick={() => { setSearch(''); setStatusFilter('ACTIVE'); }}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="table-card">
        {loading ? (
          <div className="loading-container">
            <Spinner animation="border" />
            <span className="loading-text">Loading sub-organizations...</span>
          </div>
        ) : filteredSubOrgs.length === 0 ? (
          <div className="empty-state">
            <FolderIcon />
            <p>No sub-organizations found</p>
            <Button
              variant="primary"
              className="btn-create-empty"
              onClick={isSuspended ? undefined : () => handleOpenModal()}
              disabled={isSuspended}
              style={isSuspended ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              title={isSuspended ? 'Actions are disabled - organization is suspended' : ''}
            >
              <PlusIcon />
              Create Sub-Organization
            </Button>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sub-Organization</th>
                  <th>Code</th>
                  <th>Manager</th>
                  <th>Parent</th>
                  <th>Locations</th>
                  <th>Users</th>
                  <th>Status</th>
                  <th className="th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubOrgs.map((subOrg) => (
                  <tr key={subOrg.sub_org_id}>
                    <td>
                      <div className="sub-org-cell">
                        <div className="sub-org-avatar">
                          {subOrg.sub_org_name?.charAt(0)?.toUpperCase() || 'S'}
                        </div>
                        <div className="sub-org-info">
                          <span className="sub-org-name">{subOrg.sub_org_name}</span>
                          {subOrg.description && (
                            <span className="sub-org-desc">{subOrg.description}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="code-badge">{subOrg.sub_org_code || '-'}</span>
                    </td>
                    <td>{subOrg.manager_name || '-'}</td>
                    <td>{subOrg.parent_sub_org_name || '-'}</td>
                    <td>
                      <div className="count-cell">
                        <LocationIcon />
                        <span>{subOrg.location_count || 0}</span>
                      </div>
                    </td>
                    <td>
                      <div className="count-cell">
                        <UsersIcon />
                        <span>{subOrg.user_count || 0}</span>
                      </div>
                    </td>
                    <td>{getStatusBadge(subOrg.status)}</td>
                    <td>
                      <div className="action-buttons">
                        {/* View is read-only, so it stays enabled even when suspended */}
                        <button
                          className="action-btn-icon btn-view"
                          onClick={() => navigate(`/home/sub-organizations/${subOrg.sub_org_id}`)}
                          title="View details"
                        >
                          <ViewIcon />
                        </button>
                        <button
                          className="action-btn-icon btn-edit"
                          onClick={isSuspended ? undefined : () => handleOpenModal(subOrg)}
                          title={isSuspended ? 'Actions are disabled - organization is suspended' : 'Edit'}
                          disabled={isSuspended}
                          style={isSuspended ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                        >
                          <EditIcon />
                        </button>
                        <button
                          className="action-btn-icon btn-delete"
                          onClick={isSuspended ? undefined : () => handleOpenDeleteModal(subOrg)}
                          title={
                            isSuspended
                              ? 'Actions are disabled - organization is suspended'
                              : subOrg.location_count > 0
                                ? 'Reassign or remove active locations before deleting'
                                : 'Delete'
                          }
                          // Only block on ACTIVE locations (the backend reassigns
                          // members automatically, and only refuses when active
                          // locations/child sub-orgs remain). The old `user_count`
                          // gate greyed the button out for any sub-org that ever had
                          // a member, so delete appeared broken.
                          disabled={subOrg.location_count > 0 || isSuspended}
                          style={isSuspended ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        className="custom-modal"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="modal-title-custom">
            {selectedSubOrg ? 'Edit Sub-Organization' : 'Create Sub-Organization'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            {formSuccess && <Alert variant="success">{formSuccess}</Alert>}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name <span className="required">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="sub_org_name"
                    value={formData.sub_org_name}
                    onChange={handleFormChange}
                    placeholder="e.g., Engineering Department"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="sub_org_code"
                    value={formData.sub_org_code}
                    onChange={handleFormChange}
                    placeholder="e.g., ENG-DEPT (auto-generated if empty)"
                  />
                  <Form.Text className="text-muted">
                    Leave empty to auto-generate from name
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Parent Sub-Organization</Form.Label>
                  <Form.Select
                    name="parent_sub_org_id"
                    value={formData.parent_sub_org_id}
                    onChange={handleFormChange}
                  >
                    <option value="">-- None (Top Level) --</option>
                    {parentSubOrgs
                      .filter(p => p.sub_org_id !== selectedSubOrg?.sub_org_id)
                      .map(p => (
                        <option key={p.sub_org_id} value={p.sub_org_id}>
                          {p.sub_org_name} ({p.sub_org_code})
                        </option>
                      ))
                    }
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Manager{!selectedSubOrg && <span style={{ color: '#dc2626' }}> *</span>}
                  </Form.Label>
                  <Form.Select
                    name="manager_user_id"
                    value={formData.manager_user_id}
                    onChange={handleFormChange}
                    required={!selectedSubOrg}
                  >
                    <option value="">-- Select Manager --</option>
                    {managers.map(m => (
                      <option key={m.user_id} value={m.user_id}>
                        {m.full_name} ({m.role_name})
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Selected user will manage this sub-organization
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                placeholder="Brief description of this sub-organization..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="modal-footer-custom">
            <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="btn-primary-custom"
              disabled={submitting || isSuspended}
              style={isSuspended ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              title={isSuspended ? 'Actions are disabled - organization is suspended' : ''}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  {selectedSubOrg ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                selectedSubOrg ? 'Update' : 'Create'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        className="custom-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="modal-title-custom">Delete Sub-Organization</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete <strong>{selectedSubOrg?.sub_org_name}</strong>?
          </p>
          <p className="text-muted">
            This action cannot be undone. Make sure there are no locations or users assigned to this sub-organization.
          </p>
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={isSuspended ? undefined : handleDelete}
            disabled={submitting || isSuspended}
            style={isSuspended ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            title={isSuspended ? 'Actions are disabled - organization is suspended' : ''}
          >
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SubOrgManager;
